import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/listings", label: "Our Listings" },
  { to: "/list-with-us", label: "List With Us" },
  { to: "/vacation-homes", label: "Vacation Homes" },
  { to: "/about", label: "About Us" },
  { to: "/news", label: "News" },
  { to: "/contact", label: "Contact" },
];

const HERO_PAGES = ["/", "/vacation-homes", "/list-with-us", "/dominican-republic"];

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const overHero = HERO_PAGES.includes(pathname) && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [open]);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-700 ${
          overHero ? "bg-transparent" : "border-b border-warm-border/70 bg-ivory/90 shadow-[0_1px_30px_rgba(0,0,0,0.04)] backdrop-blur-xl"
        }`}
      >
        <div className="container-luxe flex h-20 items-center justify-between lg:h-24">
          <Link to="/" className="group flex flex-col leading-none" data-testid="header-logo" aria-label="Scherif Real Estate — home">
            <span
              className={`font-serif text-[22px] font-medium tracking-[0.28em] transition-colors duration-700 lg:text-2xl ${
                overHero ? "text-ivory" : "text-charcoal"
              }`}
            >
              SCHERIF
            </span>
            <span
              className={`mt-1 font-mono text-[8px] uppercase tracking-[0.5em] transition-colors duration-700 lg:text-[9px] ${
                overHero ? "text-champagne" : "text-champagne"
              }`}
            >
              Real Estate
            </span>
          </Link>

          <nav className="hidden items-center gap-6 lg:flex" aria-label="Primary">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `link-underline font-mono text-[10px] uppercase tracking-[0.25em] transition-colors duration-500 ${
                    overHero ? "text-ivory/85 hover:text-ivory" : "text-charcoal/80 hover:text-charcoal"
                  } ${isActive ? "text-champagne" : ""}`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-4">
            <a
              href="tel:+12403724804"
              data-testid="header-call-cta"
              className={`hidden items-center gap-3 border px-5 py-3 font-mono text-[10px] uppercase tracking-[0.25em] transition-all duration-500 md:flex ${
                overHero
                  ? "border-ivory/40 text-ivory hover:border-champagne hover:text-champagne"
                  : "border-charcoal/30 text-charcoal hover:border-champagne hover:text-champagne"
              }`}
            >
              <Phone size={12} strokeWidth={1.5} aria-hidden="true" />
              Call Us&nbsp;&nbsp;240-372-4804
            </a>
            <button
              data-testid="mobile-menu-button"
              onClick={() => setOpen(true)}
              aria-label="Open menu"
              aria-expanded={open}
              className={`p-2 transition-colors duration-500 lg:hidden ${overHero ? "text-ivory" : "text-charcoal"}`}
            >
              <Menu size={26} strokeWidth={1.25} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-[60] flex flex-col bg-obsidian"
            role="dialog"
            aria-modal="true"
            aria-label="Site menu"
          >
            <div className="pointer-events-none absolute inset-3 border border-white/10 sm:inset-5" aria-hidden="true" />
            <div className="container-luxe flex h-24 items-center justify-between">
              <img src="/images/logo-white.webp" alt="Scherif Real Estate" className="h-10 w-auto" />
              <button onClick={() => setOpen(false)} aria-label="Close menu" data-testid="mobile-menu-close" className="p-2 text-ivory transition-colors hover:text-champagne">
                <X size={28} strokeWidth={1.25} />
              </button>
            </div>
            <nav className="container-luxe flex flex-1 flex-col justify-center" aria-label="Mobile">
              {NAV.map((item, i) => (
                <motion.div
                  key={item.to}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.12 + i * 0.06, duration: 0.7, ease: [0.25, 1, 0.35, 1] }}
                >
                  <NavLink
                    to={item.to}
                    data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={({ isActive }) =>
                      `group flex items-baseline gap-5 border-b border-white/10 py-4 transition-colors duration-500 ${
                        isActive ? "text-champagne" : "text-ivory hover:text-champagne"
                      }`
                    }
                  >
                    <span className="font-mono text-[10px] tracking-[0.3em] text-champagne/60">0{i + 1}</span>
                    <span className="font-serif text-3xl font-light italic tracking-wide transition-transform duration-500 group-hover:translate-x-2 sm:text-4xl">
                      {item.label}
                    </span>
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.55, duration: 0.6 }}
              className="container-luxe space-y-5 pb-14"
            >
              <p className="font-mono text-[9px] uppercase tracking-[0.4em] text-ivory/40">Soby &amp; Associates — DC • Maryland • Virginia</p>
              <a
                href="tel:+12403724804"
                data-testid="mobile-menu-call-cta"
                className="flex items-center justify-center gap-3 bg-champagne py-5 font-mono text-xs uppercase tracking-[0.3em] text-obsidian transition-colors duration-500 hover:bg-ivory"
              >
                <Phone size={14} strokeWidth={1.5} aria-hidden="true" />
                Call 240-372-4804
              </a>
              <a
                href="mailto:Sobeida@realestatebyscherif.com"
                data-testid="mobile-menu-email"
                className="block text-center font-mono text-[10px] uppercase tracking-[0.25em] text-ivory/50 transition-colors hover:text-champagne"
              >
                Sobeida@realestatebyscherif.com
              </a>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="fixed inset-x-0 bottom-0 z-40 md:hidden" data-testid="mobile-sticky-call">
        <a
          href="tel:+12403724804"
          data-testid="mobile-sticky-call-cta"
          className="flex items-center justify-center gap-3 bg-charcoal/95 py-4 font-mono text-[11px] uppercase tracking-[0.3em] text-ivory backdrop-blur"
        >
          <Phone size={13} strokeWidth={1.5} aria-hidden="true" />
          Call 240-372-4804
        </a>
      </div>
    </>
  );
};

export default Header;
